import { useFormik } from 'formik';
import { ChangeEvent, useEffect, useState } from 'react';
import usePostNote from '../../../API/hooks/notes/usePostNote';
import { PostNoteRequest } from '../../../API/types/Notes';
import Input from '../../common/Input/Input';
import { Button } from '../../ui/Button/Button';
import S from './CreateNoteStyles';
import { NoteTitles } from '../../../API/services/NotesService';

type Props = {
  fleetId: string;
  entityId: string;
  entityName: string;
  title?: string;
  text?: string;
  editableTitle?: boolean;
  isAutoGenerated?: boolean;
  toggleModal: (closed: boolean) => void;
  onClose?: any;
};

function CreateNote({
  fleetId, entityId, entityName, title, text, editableTitle, isAutoGenerated, toggleModal, onClose,
}: Props): JSX.Element {
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const onSubmitSuccess = (): void => {
    toggleModal(false);
    onClose?.();
  };
  const onSubmitFail = (errorMessage: string): void => setSubmitError(errorMessage);

  // formik can't process async actions and work with server response,
  // thus we have to pass callbacks to tanstack mutation
  const { mutate } = usePostNote(onSubmitSuccess, onSubmitFail);

  const formik = useFormik({
    initialValues: {
      fleetId,
      entityId,
      entityName,
      title: title || NoteTitles[entityName],
      text: text || '',
      isAutoGenerated: isAutoGenerated || false,
    },
    onSubmit: (values) => mutate({ ...values } as unknown as PostNoteRequest),
    validate: (values) => {
      const errors = {} as Record<string, string | undefined>;

      if (!values.text.length) errors.text = 'Note can\'t be empty';
      return errors;
    },
    validateOnMount: true,
  });

  // if there is onSubmit error formik will stack in submitting state
  // blocking further interactions with submit button
  useEffect(() => {
    formik.setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  const handleInputChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
    setSubmitError('');
  };

  return (
    <S.Wrapper>
      <form onSubmit={formik.handleSubmit}>
        <div className="details-container">
          <S.RowContainer>
            <Input
              label="Title"
              name="title"
              placeholder="Type a title for note (optional)"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.title}
              disabled={!editableTitle && (title?.length as unknown as boolean)}
            />
          </S.RowContainer>
          <S.RowContainer>
            <Input
              label="Note"
              name="text"
              placeholder="Describe your point here"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.text}
              error={formik.touched.text && formik.errors.text}
            />
          </S.RowContainer>
          <S.RowContainer>
            <div className="actions">
              <Button
                height="50px"
                onClick={() => {
                  toggleModal(false);
                  onClose();
                }}
                type="button"
              >
                Cancel
              </Button>
              <Button
                className="add-button"
                height="50px"
                type="submit"
                disabled={formik.isSubmitting || !formik.isValid || !!submitError}
              >
                Add
              </Button>
            </div>
          </S.RowContainer>

          {!!submitError && (
            typeof submitError === 'string'
              ? <S.ErrorMessage>{submitError}</S.ErrorMessage>
              : submitError.map((error) => <S.ErrorMessage>{error}</S.ErrorMessage>)
          )}
        </div>
      </form>
    </S.Wrapper>
  );
}

export default CreateNote;
